import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DndProvider } from "react-dnd/dist/core";
import { useDrag } from "react-dnd/dist/hooks";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
    Button,
    Switch,
    FormControlLabel,
    Typography,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";

import API from "../api/axiosInstance";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/PageDesigner.css";

import { PageField, Control, Panel, TablePage, isField, isControl, ItemTypes, RelatedTableControl } from "../types/interfaces";
import ControlProperties from "../components/pagebuilder/ControlProperties";
import PanelColumn from "../components/pagebuilder/PanelColumn";

import LabelIcon from "@mui/icons-material/Label";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ListIcon from "@mui/icons-material/List";
import CodeIcon from "@mui/icons-material/Code";
import { getPanelAtPath } from "../utils/layoutUtils";
import { deletePanelAtPath } from "../utils/layoutUtils";
import { TableRelationship, Table } from "../types/interfaces"; // Already exists, just making sure
import TableChartIcon from "@mui/icons-material/TableChart"; // For Table drag icon
import SaveStatusDialog from "../components/SaveStatusDialog";


const controlTypes = [
    { type: "LABEL", label: "Label", icon: <LabelIcon fontSize="small" /> },
    { type: "LINE", label: "Line", icon: <ViewHeadlineIcon fontSize="small" /> },
    { type: "PANEL", label: "Panel", icon: <ViewModuleIcon fontSize="small" /> },
    { type: "TEXTBOX", label: "Textbox", icon: <TextFieldsIcon fontSize="small" /> },
    { type: "CHECKBOX", label: "Checkbox", icon: <CheckBoxIcon fontSize="small" /> },
    { type: "RADIO", label: "Radio Button", icon: <RadioButtonCheckedIcon fontSize="small" /> },
    { type: "LISTBOX", label: "Listbox", icon: <ListIcon fontSize="small" /> },
    { type: "COMBOBOX", label: "Combobox", icon: <ListIcon fontSize="small" /> },
    { type: "CUSTOM_HTML", label: "Custom HTML", icon: <CodeIcon fontSize="small" /> }
];

const PageDesigner: React.FC = () => {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const selectedPage = searchParams.get("pageid");
    const mainRef = useRef(null);
    const [fields, setFields] = useState<PageField[]>([]);
    const [relationships, setRelationships] = useState<TableRelationship[]>([]);
    const [pendingEdits, setPendingEdits] = useState<PageField | Control | Panel | null>(null);
    const [panels, setPanels] = useState<Panel[]>([]);
    const [selectedControl, setSelectedControl] = useState<PageField | Control | Panel | null>(null);
    const [pageMetadata, setPageMetadata] = useState<TablePage | null>(null);
    const [isPublished, setIsPublished] = useState(false);
    const [toolboxTab, setToolboxTab] = useState(0);
    const [customJS, setCustomJS] = useState("");
    const [customCSS, setCustomCSS] = useState("");

    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'saving' | 'success' | 'error'>('saving');
    const [saveMessage, setSaveMessage] = useState<string>("");

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!mainRef.current) return;
            const target = e.target as HTMLElement;
            if (
                !target.closest(".control-box") &&
                !target.closest(".panel-component") &&
                !target.closest(".right-panel")
            ) {
                if (selectedControl && window.confirm("Apply changes to this control before continuing?")) {
                    setSelectedControl(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [selectedControl]);

    useEffect(() => {
        if (!selectedPage) return;

        const loadPage = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user') || "{}");
                const headers = {
                    headers: {
                        authToken: user.user.authtoken,
                        sessionId: user.user.sessionId
                    }
                };

                const response = await API.get(`/pages/${selectedPage}`, headers);
                const page = response.data;

                const parsedLayout = page.layout ? JSON.parse(page.layout) : null;

                setPageMetadata(page);

                setIsPublished(page.isPublished || false);
                setPanels(parsedLayout?.root || []);
                setCustomJS(parsedLayout?.customJS || "");
                setCustomCSS(parsedLayout?.customCSS || "");


                if (page.tableID) {
                    await fetchTableData(page.tableID, headers);
                }

            } catch (err) {
                console.error("Error loading page:", err);
            }
        };

        loadPage();
    }, [selectedPage]);


    const fetchTableData = async (tableId: string, headers: any) => {
        try {
            const tableRes = await API.get(`/tables/${tableId}`, headers);
            const table = tableRes.data;

            if (!table || !table.tableID) {
                console.warn("Table not found or invalid response", tableRes.data);
                return;
            }



            // Defensive fallback
            const cleanedFields = Array.isArray(table.fields) ? table.fields : [];
            const cleanedRelationships = Array.isArray(table.relationships) ? table.relationships : [];

            console.log("Cleaned fields:", cleanedFields);

            setFields(cleanedFields);
            setRelationships(cleanedRelationships);

        } catch (err: any) {
            console.error("Error fetching table", err);

        
        }
    };


    const handleDrop = (
        item: PageField | Control | Panel,
        panelPath: number[],
        colIndex: number
    ) => {
        if (!item || typeof item !== "object") return;

        const updated = [...panels];
        const targetPanel = getPanelAtPath(updated, panelPath);
        if (!targetPanel) return;

        const panelCountInPath = panelPath.length;
        const columnContents = targetPanel.controls.filter(c => c.column === colIndex);
        const containsPanel = columnContents.some(c => c.field && "type" in c.field && c.field.type === "PANEL");
        const containsNonPanel = columnContents.some(c => c.field && (!("type" in c.field) || c.field.type !== "PANEL"));
        const containsOnlyPanel = columnContents.length === 1 && columnContents[0].field?.type === "PANEL";

        // Determine type of drop item
        const isPanel = "type" in item && item.type === "PANEL";
        const isRelatedTable = "type" in item && item.type === "RELATED_TABLE";
        const isAField = isField(item);
        //const isAControl = isControl(item);

        // Rule 1: Panel nesting depth
        if (isAField && panelCountInPath > 1) {
            alert("You can only nest a panel one level deep.");
            return;
        }

        // Rule 2: Can't drop anything non-panel into a column with a panel
        if (!isPanel && containsPanel) {
            alert("You cannot drop a field/control into a column that already has a panel.");
            return;
        }

        // Rule 3: Can't drop a panel into a column with other controls
        if (isPanel && containsNonPanel) {
            alert("You cannot drop a panel into a column that already has fields/controls.");
            return;
        }

        // Block multiple PANELS in a column
        if (isPanel && containsPanel) {
            alert("Only one panel is allowed per column.");
            return;
        }


        let controlToAdd;

        const generatedId = generateFriendlyId(selectedPage, item);

        if (isPanel) {
            controlToAdd = {
                id: generatedId,
                column: colIndex,
                row: 0,
                field: {
                    id: generatedId,
                    panelId: generatedId,
                    type: "PANEL",
                    name: item.fieldName || "Nested Panel",
                    row: 0,
                    column: colIndex,
                    columns: 2,
                    rows: 1,
                    controls: []
                } as Panel
            };

        } else if (isRelatedTable) {
            controlToAdd = {
                id: generatedId,
                column: colIndex,
                row: 0,
                field: {
                    id: generatedId,
                    type: "RELATED_TABLE",
                    label: item.label || "Related Table",
                    name: item.fieldName || "relatedTable",
                    relationshipId: (item as any).relationshipID || "",
                    column: colIndex,
                    row: 0,
                    fixedFilter: "",
                    fieldLinks: [],
                    displayFields: [],
                    allowView: true,
                    allowAdd: true,
                    allowInlineAdd: false,
                    allowEdit: true,
                    allowInlineEdit: false,
                    allowDelete: true
                } as RelatedTableControl
            };
        } else {
            // Field or generic Control
            controlToAdd = {
                id: generatedId,
                column: colIndex,
                row: 0,
                field: {
                    ...item,
                    id: generatedId,
                    column: colIndex,
                    row: 0
                }
            };

        }

        targetPanel.controls.push(controlToAdd);
        setPanels(updated);

        // at the end of handleDrop()
        setSelectedControl(controlToAdd.field);

    };


    const handleSave = async () => {
        if (!pageMetadata || !selectedPage) {
            setSaveStatus("error");
            setSaveMessage("Missing page context.");
            setSaveDialogOpen(true);
            return;
        }

        setSaveStatus("saving");
        setSaveDialogOpen(true);
        setSaveMessage("");

        try {
            const user = JSON.parse(localStorage.getItem('user') || "{}");
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            const payload = {
                ...pageMetadata,
                layout: JSON.stringify({
                    root: panels,
                    customCSS,
                    customJS,
                }),
                isPublished,
            };

            await API.put(`/pages/${selectedPage}`, payload, headers);

            setSaveStatus("success");
            setTimeout(() => {
                setSaveDialogOpen(false);
                setSaveStatus("success");
            }, 4000);
        } catch (err: any) {
            console.error("Save failed", err);
            setSaveStatus("error");

            const errorMsg = err?.response?.data;
            if (typeof errorMsg === "string") {
                setSaveMessage(errorMsg);
            } else {
                setSaveMessage(JSON.stringify(errorMsg));
            }
        }
    };


    const handleTest = () => {
        const win = window.open("", "_blank");
        if (win) {
            win.document.write(`<pre>${JSON.stringify({ panels, customJS, customCSS }, null, 2)}</pre>`);
        }
    };

    const handlePublishToggle = () => {
        const message = isPublished
            ? "Are you sure you want to unpublish this page?"
            : "Are you sure you want to publish this page?";

        if (!window.confirm(message)) return;

        const user = JSON.parse(localStorage.getItem('user') || "{}");
        const headers = {
            headers: {
                authToken: user.user.authtoken,
                sessionId: user.user.sessionId
            }
        };
        

        const endpoint = isPublished
        ? `/pages/${selectedPage}/unpublish`
        : `/pages/${selectedPage}/publish`;

        try {
            API.put(endpoint,  headers);
            if(isPublished)
                {
                    setIsPublished(!isPublished);
                }
                else
                {
                    setIsPublished(!isPublished);
                }
        } catch {
            alert("Failed to change page publish state.");
        } finally {

        }
    };


    const DraggableField = ({ field }: { field: PageField }) => {
        const ref = useRef<HTMLDivElement>(null);
        const [, drag] = useDrag(() => ({
            type: ItemTypes.FIELD,
            item: field
        }));

        drag(ref); // attach the drag behavior to this DOM node

        return <div ref={ref} className="draggable-item">{field.fieldName}</div>;
    };



    const movePanel = (index: number, direction: number) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= panels.length) return;

        const updated = [...panels];
        const [moved] = updated.splice(index, 1);
        updated.splice(newIndex, 0, moved);
        setPanels(updated);
    };


    const DraggableControl = ({ type, label, icon }: { type: string; label: string; icon: React.ReactNode }) => {
        const ref = useRef<HTMLDivElement>(null);
        const [, drag] = useDrag(() => ({
            type: ItemTypes.CONTROL,
            item: { type, label }
        }));

        drag(ref);

        return <div ref={ref} className="draggable-item">{icon} {label}</div>;
    };

    const DraggableTable = ({ relationship }: { relationship: TableRelationship }) => {
        const ref = useRef<HTMLDivElement>(null);
        const [, drag] = useDrag(() => ({
            type: ItemTypes.CONTROL,
            item: {
                type: "TABLE",
                label: relationship.childTableID,
                relationshipID: relationship.relationshipID
            }
        }));

        drag(ref);
        return (
            <div ref={ref} className="draggable-item">
                <TableChartIcon fontSize="small" style={{ marginRight: 6 }} />
                {relationship.childTableID}
            </div>
        );
    };



    return (
        <DndProvider backend={HTML5Backend}>
            <Header />
            <div className="page-designer" 
                style={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    "--main-bg": theme.palette.background.default,
                    "--left-panel-bg": theme.palette.background.paper,
                    "--right-panel-bg": theme.palette.background.paper,
                    "--panel-bg": theme.palette.background.paper,
                    "--header-bg": theme.palette.background.paper
                } as React.CSSProperties}>
                <div className="designer-container">

                    {/* Header */}
                    <div className="designer-header">
                        <div className="left-controls">
                            <Button variant="contained" onClick={handleSave}>Save</Button>
                            <Button variant="outlined" onClick={handleTest}>Test</Button>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPublished}
                                        onChange={handlePublishToggle}
                                    />
                                }
                                label={isPublished ? "Published" : "Unpublished"}
                            />
                        </div>
                        <div className="page-info">
                            <Typography variant="h6">Page: {pageMetadata?.pageName}</Typography>
                            <Typography variant="subtitle2">Type: {pageMetadata?.pageType}</Typography>
                        </div>
                    </div>

                    {/* Toolbox / Scripts */}
                    <div className="left-panel">
                        <Tabs
                            orientation="horizontal"
                            variant="fullWidth"
                            value={toolboxTab}
                            onChange={(e, newVal) => setToolboxTab(newVal)}
                        >
                            <Tab label="Toolbox" />
                            <Tab label="Scripts" />
                        </Tabs>

                        {toolboxTab === 0 && (
                            <div className="toolbox-content">
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>Fields</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {fields
                                            .map(field => (
                                                <DraggableField key={field.fieldID} field={field} />
                                            ))}

                                    </AccordionDetails>
                                   

                                </Accordion>
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>Related</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {relationships.map((rel) => (
                                            <DraggableTable key={rel.relationshipID} relationship={rel} />
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>Controls</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {controlTypes.map(({ type, label, icon }) => (
                                            <DraggableControl key={type} type={type} label={label} icon={icon} />
                                        ))}

                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        )}

                        {toolboxTab === 1 && (
                            <div className="scripts-editor">
                                <Typography variant="h6">Custom JavaScript</Typography>
                                <textarea value={customJS} onChange={(e) => setCustomJS(e.target.value)} rows={5} />
                                <Typography variant="h6">Custom CSS</Typography>
                                <textarea value={customCSS} onChange={(e) => setCustomCSS(e.target.value)} rows={5} />
                            </div>
                        )}
                    </div>

                    {/* Main Designer Area */}
                    <div className="main-layout" ref={mainRef}>
                        {panels.map((panel, index) => (
                            <div key={panel.id} className={`panel-component ${selectedControl?.fieldID === panel.id ? "selected" : ""}`}>
                                <div className="panel-header-toolbar">
                                    <div>{panel.name || panel.panelId} ({panel.columns} Columns)</div>
                                    <div>
                                        <SettingsIcon sx={{ color: theme.palette.text.primary }} onClick={() => {
                                            setSelectedControl({
                                                ...panel,
                                                type: "PANEL"
                                            });
                                        }}
 />
                                        <ArrowUpwardIcon sx={{ color: theme.palette.text.primary }} onClick={() => movePanel(index, -1)} />
                                        <ArrowDownwardIcon sx={{ color: theme.palette.text.primary }} onClick={() => movePanel(index, 1)} />
                                        <CloseIcon sx={{ color: theme.palette.text.primary }} onClick={() => {
                                            if (window.confirm("Delete this panel?")) {
                                                const updated = deletePanelAtPath(panels, [index]);
                                                setPanels(updated);
                                            }
                                        }} />


                                    </div>
                                </div>
                                <div className="panel-columns">
                                    {Array.from({ length: panel.columns }).map((_, colIndex) => {
                                        const content = panel.controls.filter(c => c.column === colIndex);
                                        return (
                                            <PanelColumn
                                                key={colIndex}
                                                panel={panel}
                                                selectedControl={selectedControl}
                                                colIndex={colIndex}
                                                panelPath={[index]}
                                                content={content}
                                                onDrop={(item) => handleDrop(item, [index], colIndex)}
                                                onOpenSettings={(item) => setSelectedControl(item)}
                                                onDelete={(panel, controlId) => {
                                                    const updated = [...panels];
                                                    const panelIndex = updated.findIndex(p => p.id === panel.id);
                                                    if (panelIndex === -1) return;
                                                    updated[panelIndex].controls = updated[panelIndex].controls.filter(c => c.id !== controlId);
                                                    setPanels(updated);
                                                }}


                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                        <Button variant="outlined" onClick={() => setPanels([...panels, {
                            id: `panel-${Date.now()}`,
                            panelId: `panel-${panels.length + 1}`,
                            columns: 2,
                            row: 1,
                            rows: 1,
                            controls: []
                        }])}>+ Add Panel</Button>
                    </div>

                    {/* Properties Panel - only show if something is selected */}
                    {selectedControl && (
                        <div className="right-panel">
                            <ControlProperties
                                control={pendingEdits || selectedControl}
                                onChange={setPendingEdits}
                                relationships={relationships}
                            />

                            <button
                                className="btn-save"
                                disabled={!pendingEdits || pendingEdits === selectedControl}
                                onClick={() => {
                                    const updatedPanels = [...panels];

                                    const findAndUpdate = (controls: any[]) => {
                                        for (let i = 0; i < controls.length; i++) {
                                            if (controls[i].field?.fieldID === pendingEdits?.fieldID) {
                                                controls[i].field = pendingEdits;
                                                return true;
                                            }

                                            if (controls[i].field && "controls" in controls[i].field) {
                                                if (findAndUpdate((controls[i].field as Panel).controls)) return true;
                                            }
                                        }
                                        return false;
                                    };

                                    updatedPanels.forEach(p => findAndUpdate(p.controls));
                                    setPanels(updatedPanels);
                                    setSelectedControl(pendingEdits);
                                    setPendingEdits(null);
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <SaveStatusDialog
                open={saveDialogOpen}
                status={saveStatus}
                message={saveMessage}
                onClose={() => {
                    setSaveDialogOpen(false);
                    setSaveMessage('');
                }}
            />

        </DndProvider>
    );
};

let controlCount = 1;

function generateFriendlyId(pageName: string | null, obj: any): string {
    const safePage = pageName || "Page";
    let base = safePage;

    if ("name" in obj && typeof obj.name === "string") {
        base += `_${obj.name}`;
    } else if ("label" in obj && typeof obj.label === "string") {
        base += `_${obj.label.replace(/\s+/g, "")}`;
    }

    if ("type" in obj && typeof obj.type === "string") {
        base += `_${obj.type}`;
    }

    return `${base}_${controlCount++}`;
}


export default PageDesigner;
